"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "baseModelInsert", {
    enumerable: true,
    get: function() {
        return baseModelInsert;
    }
});
const _dbHelpers = require("src/helpers/dbHelpers");
const _nocodbsdk = require("nocodb-sdk");
const _attachmenturluploadpreparator = require("./attachment-url-upload-preparator");
const _uniqueConstraintErrorHandler = require("~/helpers/uniqueConstraintErrorHandler");
const _getAst = /*#__PURE__*/ _interop_require_default(require("~/helpers/getAst"));
const _utils = require("~/utils");
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
const baseModelInsert = (baseModel)=>{
    const single = async (data, request, trx, _disableOptimization = false)=>{
        let insertObj;
        try {
            const columns = await baseModel.model.getColumns(baseModel.context);
            const dbDataWrapper = (0, _dbHelpers.dataWrapper)(data);
            // exclude auto increment columns in body
            for (const col of columns){
                if (col.ai) {
                    const keyName = dbDataWrapper.getColumnKeyName(col);
                    if (data[keyName]) {
                        delete data[keyName];
                    }
                }
            }
            await (0, _dbHelpers.populatePk)(baseModel.context, baseModel.model, data);
            // todo: filter based on view
            insertObj = await baseModel.model.mapAliasToColumn(baseModel.context, data, baseModel.clientMeta, baseModel.dbDriver, columns);
            await baseModel.validate(insertObj, columns);
            if ('beforeInsert' in baseModel) {
                await baseModel.beforeInsert(insertObj, trx, request);
            }
            await baseModel.prepareNocoData(insertObj, true, request);
            let response;
            // const driver = trx ? trx : baseModel.dbDriver;
            const query = baseModel.dbDriver(baseModel.tnPath).insert(insertObj);
            if (baseModel.isPg && baseModel.model.primaryKey) {
                query.returning(`${baseModel.model.primaryKey.column_name} as ${baseModel.model.primaryKey.id}`);
                response = await baseModel.execAndParse(query, null, {
                    raw: true
                });
            }
            const ai = baseModel.model.columns.find((c)=>c.ai);
            let ag;
            if (!ai) ag = baseModel.model.columns.find((c)=>{
                var _c_meta;
                return (_c_meta = c.meta) == null ? void 0 : _c_meta.ag;
            });
            // handle if autogenerated primary key is used
            if (ag) {
                if (!response) {
                    await baseModel.execAndParse(query);
                }
                response = await baseModel.readByPk(baseModel.extractCompositePK({
                    rowId: insertObj[ag.column_name],
                    insertObj,
                    ag,
                    ai
                }), false, {}, {
                    ignoreView: true,
                    getHiddenColumn: true
                });
            } else if (!response || typeof (response == null ? void 0 : response[0]) !== 'object' && (response == null ? void 0 : response[0]) !== null) {
                let id;
                if (response == null ? void 0 : response.length) {
                    id = response[0];
                } else {
                    var _ref, _ref1;
                    var _res_;
                    const res = await baseModel.execAndParse(query, null, {
                        raw: true
                    });
                    id = (_ref = (_ref1 = res == null ? void 0 : res.id) != null ? _ref1 : (_res_ = res[0]) == null ? void 0 : _res_.insertId) != null ? _ref : res;
                }
                if (ai) {
                    if (baseModel.isSqlite) {
                        var _this;
                        // sqlite doesnt return id after insert
                        id = (_this = await baseModel.execAndParse(baseModel.dbDriver(baseModel.tnPath).select(ai.column_name).max(ai.column_name, {
                            as: '__nc_ai_id'
                        }), null, {
                            raw: true,
                            first: true
                        })) == null ? void 0 : _this.__nc_ai_id;
                    } else if (baseModel.isSnowflake || baseModel.isDatabricks) {
                        id = (await baseModel.execAndParse(baseModel.dbDriver(baseModel.tnPath).max(ai.column_name, {
                            as: '__nc_ai_id'
                        }), null, {
                            raw: true,
                            first: true
                        })).__nc_ai_id;
                    }
                    response = await baseModel.readByPk(baseModel.extractCompositePK({
                        rowId: id,
                        insertObj,
                        ag,
                        ai
                    }), false, {}, {
                        ignoreView: true,
                        getHiddenColumn: true
                    });
                } else {
                    response = data;
                }
            } else if (ai) {
                var _response_;
                const id = Array.isArray(response) ? response == null ? void 0 : (_response_ = response[0]) == null ? void 0 : _response_[ai.id] : response == null ? void 0 : response[ai.id];
                response = await baseModel.readByPk(baseModel.extractCompositePK({
                    rowId: id,
                    insertObj,
                    ag,
                    ai
                }), false, {}, {
                    ignoreView: true,
                    getHiddenColumn: true
                });
            }
            await baseModel.afterInsert({
                data: response,
                insertData: data,
                trx,
                req: request
            });
            await baseModel.statsUpdate({
                count: 1
            });
            return Array.isArray(response) ? response[0] : response;
        } catch (e) {
            // Handle unique constraint violations - this will throw if it's a unique constraint error
            await (0, _uniqueConstraintErrorHandler.handleUniqueConstraintError)({
                error: e,
                baseModel: baseModel,
                insertData: insertObj || data
            });
            await baseModel.errorInsert(e, data, trx, request);
            throw e;
        }
    };
    const bulk = async (datas, { chunkSize: _chunkSize = 100, cookie, foreign_key_checks = true, skip_hooks = false, raw = false, insertOneByOneAsFallback = false, isSingleRecordInsertion = false, typecast = false, allowSystemColumn = false, undo = false, apiVersion = _nocodbsdk.NcApiVersion.V2 } = {})=>{
        let trx;
        try {
            const insertDatas = raw ? datas : [];
            const postInsertOpsMap = {};
            let preInsertOps = [];
            let aiPkCol;
            let agPkCol;
            let columns;
            if (!raw) {
                columns = await baseModel.model.getColumns(baseModel.context);
                const order = await baseModel.getHighestOrderInTable();
                const nestedCols = columns.filter((c)=>(0, _nocodbsdk.isLinksOrLTAR)(c));
                const attachmentCols = columns.filter((c)=>(0, _nocodbsdk.isAttachment)(c));
                for (const [index, d] of datas.entries()){
                    const insertObj = await baseModel.handleValidateBulkInsert(d, columns, {
                        allowSystemColumn,
                        undo,
                        typecast
                    });
                    await baseModel.prepareNocoData(insertObj, true, cookie, null, {
                        ncOrder: order == null ? void 0 : order.plus(index),
                        undo
                    });
                    // prepare nested link data for insert only if it is single record insertion
                    if (isSingleRecordInsertion || apiVersion === _nocodbsdk.NcApiVersion.V3) {
                        const operations = await baseModel.prepareNestedLinkQb({
                            nestedCols,
                            data: d,
                            insertObj,
                            req: cookie
                        });
                        postInsertOpsMap[index] = operations.postInsertOps;
                        preInsertOps = operations.preInsertOps;
                    }
                    if (attachmentCols.length > 0) {
                        var _postInsertOpsMap_index, _attachmentOperations_postInsertOps, _attachmentOperations_preInsertOps;
                        const attachmentOperations = await new _attachmenturluploadpreparator.AttachmentUrlUploadPreparator().prepareAttachmentUrlUpload(baseModel, {
                            attachmentCols,
                            data: insertObj,
                            req: cookie
                        });
                        postInsertOpsMap[index] = [
                            ...(_postInsertOpsMap_index = postInsertOpsMap[index]) != null ? _postInsertOpsMap_index : [],
                            ...(_attachmentOperations_postInsertOps = attachmentOperations.postInsertOps) != null ? _attachmentOperations_postInsertOps : []
                        ];
                        preInsertOps = [].concat(...preInsertOps != null ? preInsertOps : [], ...(_attachmentOperations_preInsertOps = attachmentOperations.preInsertOps) != null ? _attachmentOperations_preInsertOps : []);
                    }
                    insertDatas.push(insertObj);
                }
                aiPkCol = baseModel.model.primaryKeys.find((pk)=>pk.ai);
                agPkCol = baseModel.model.primaryKeys.find((pk)=>{
                    var _pk_meta;
                    return (_pk_meta = pk.meta) == null ? void 0 : _pk_meta.ag;
                });
            } else {
                columns = await baseModel.model.getColumns(baseModel.context);
                const order = await baseModel.getHighestOrderInTable();
                await Promise.all(insertDatas.map(async (d, i)=>await baseModel.prepareNocoData(d, true, cookie, null, {
                        raw,
                        undo: undo,
                        ncOrder: order == null ? void 0 : order.plus(i)
                    })));
            }
            if ('beforeBulkInsert' in baseModel) {
                await baseModel.beforeBulkInsert(insertDatas, trx, cookie, {
                    allowSystemColumn
                });
            }
            // await baseModel.beforeInsertb(insertDatas, null);
            // fallbacks to `10` if database client is sqlite
            // to avoid `too many SQL variables` error
            // refer : https://www.sqlite.org/limits.html
            const chunkSize = baseModel.isSqlite ? 10 : _chunkSize;
            trx = await baseModel.dbDriver.transaction();
            if (!foreign_key_checks) {
                if (baseModel.isPg) {
                    await trx.raw('set session_replication_role to replica;');
                } else if (baseModel.isMySQL) {
                    await trx.raw('SET foreign_key_checks = 0;');
                }
            }
            await baseModel.runOps(preInsertOps.map((f)=>f()), trx);
            let responses;
            // insert one by one as fallback to get ids for sqlite and mysql
            if (insertOneByOneAsFallback && (baseModel.isSqlite || baseModel.isMySQL)) {
                // sqlite and mysql doesn't support returning, so insert one by one and return ids
                responses = [];
                for (const insertData of insertDatas){
                    const query = trx(baseModel.tnPath).insert(insertData);
                    let id = (await query)[0];
                    if (agPkCol) {
                        id = insertData[agPkCol.column_name];
                    }
                    responses.push(baseModel.extractCompositePK({
                        rowId: id,
                        ai: aiPkCol,
                        ag: agPkCol,
                        insertObj: insertData,
                        force: true
                    }) || insertData);
                }
            } else {
                var _baseModel_model_primaryKeys;
                const returningObj = {};
                for (const col of baseModel.model.primaryKeys){
                    returningObj[col.title] = col.column_name;
                }
                responses = !raw && baseModel.isPg ? await trx.batchInsert(baseModel.tnPath, insertDatas, chunkSize).returning(((_baseModel_model_primaryKeys = baseModel.model.primaryKeys) == null ? void 0 : _baseModel_model_primaryKeys.length) ? returningObj : '*') : await trx.batchInsert(baseModel.tnPath, insertDatas, chunkSize);
            }
            if (!foreign_key_checks) {
                if (baseModel.isPg) {
                    await trx.raw('set session_replication_role to origin;');
                } else if (baseModel.isMySQL) {
                    await trx.raw('SET foreign_key_checks = 1;');
                }
            }
            // insert nested link data for single record insertion or v3
            if (isSingleRecordInsertion || apiVersion === _nocodbsdk.NcApiVersion.V3) {
                for(let i = 0; i < responses.length; i++){
                    var _baseModel_model_primaryKey;
                    const row = responses[i];
                    let rowId = row[(_baseModel_model_primaryKey = baseModel.model.primaryKey) == null ? void 0 : _baseModel_model_primaryKey.title];
                    if (aiPkCol || agPkCol) {
                        rowId = baseModel.extractCompositePK({
                            rowId,
                            ai: aiPkCol,
                            ag: agPkCol,
                            insertObj: insertDatas[i]
                        });
                    }
                    await baseModel.runOps(postInsertOpsMap[i].map((f)=>f(rowId)), trx);
                }
            }
            await trx.commit();
            if (!raw && !skip_hooks) {
                if (isSingleRecordInsertion) {
                    const insertData = await baseModel.readByPk(responses[0]);
                    await baseModel.afterInsert({
                        data: insertData,
                        insertData: datas == null ? void 0 : datas[0],
                        trx: baseModel.dbDriver,
                        req: cookie
                    });
                } else {
                    const insertResponseList = await baseModel.chunkList({
                        pks: responses.map((d)=>baseModel.extractPksValues(d))
                    });
                    // get ast
                    const { ast, parsedQuery } = await (0, _getAst.default)(baseModel.context, {
                        model: baseModel.model,
                        query: {},
                        extractOnlyPrimaries: false
                    });
                    // nocoexecute
                    const insertResponses = await (0, _utils.nocoExecute)(ast, insertResponseList, {}, parsedQuery);
                    await baseModel.afterBulkInsert(insertResponses, baseModel.dbDriver, cookie);
                }
            }
            await baseModel.statsUpdate({
                count: insertDatas.length
            });
            return responses;
        } catch (e) {
            await (trx == null ? void 0 : trx.rollback());
            // Handle unique constraint violations - this will throw if it's a unique constraint error
            await (0, _uniqueConstraintErrorHandler.handleUniqueConstraintError)({
                error: e,
                baseModel: baseModel,
                insertData: datas == null ? void 0 : datas[0]
            });
            // await baseModel.errorInsertb(e, data, null);
            throw e;
        }
    };
    return {
        single,
        bulk
    };
};
